import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { fetcher } from "matsuri-hooks";
import { assertIsDefined } from "../../helpers/assetIsDefined";
import { signInWithGoogle, signUpWithGoogle, } from "../../endpoints/matsuri-forms.v1";
const checkJwtExpiration = (jwt) => {
    const splitted = jwt.split(".");
    if (splitted.length !== 3)
        return false;
    const [, payload] = splitted;
    const parsed = JSON.parse(atob(payload));
    return new Date() < new Date(parsed.exp * 1000);
};
const MATSURI_FORMS_TOKEN_KEY = "matsuri-forms-token";
const storage = {
    get: () => {
        return window.localStorage.getItem(MATSURI_FORMS_TOKEN_KEY);
    },
    set: (token) => {
        window.localStorage.setItem(MATSURI_FORMS_TOKEN_KEY, token);
    },
    clear: () => {
        window.localStorage.removeItem(MATSURI_FORMS_TOKEN_KEY);
    },
};
const AuthContext = createContext(null);
const genErrorMessage = (error) => {
    if (error.message.includes("user_not_found")) {
        return "ユーザーが存在しません。ユーザー登録をしてください。";
    }
    if (error.message.includes("user_already_exists")) {
        return "既にアカウントが存在します。ログインしてください。";
    }
    return "サーバーエラーの可能性があります。しばらく時間をおいて再度お試しください。";
};
const exchangeCodeToToken = async (code, mode) => {
    return fetcher(mode === "signup" ? signUpWithGoogle() : signInWithGoogle(), {
        method: "POST",
        body: JSON.stringify({
            code,
        }),
    });
};
const getInitialToken = () => {
    const stored = storage.get();
    if (stored !== null && checkJwtExpiration(stored)) {
        return stored;
    }
    storage.clear();
    return "";
};
export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(getInitialToken);
    const [error, setError] = useState();
    const [codeClient, setCodeClient] = useState();
    const updateToken = useCallback((newToken) => {
        storage.set(newToken);
        setToken(newToken);
    }, []);
    const authMode = useRef("login");
    const initClient = useCallback(() => {
        assertIsDefined(process.env.CLIENT_ID);
        const client = google.accounts.oauth2.initCodeClient({
            client_id: process.env.CLIENT_ID,
            scope: [
                "https://www.googleapis.com/auth/spreadsheets",
                "https://www.googleapis.com/auth/userinfo.profile",
                "https://www.googleapis.com/auth/userinfo.email",
            ].join(" "),
            ux_mode: "popup",
            redirect_uri: "postmessage",
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            callback: async (response) => {
                const { code } = response;
                const { data, error } = await exchangeCodeToToken(code, authMode.current);
                if (error) {
                    console.error(error);
                    setError(new Error(genErrorMessage(error), { cause: error }));
                }
                if (data?.token !== undefined) {
                    updateToken(data?.token);
                }
            },
        });
        setCodeClient(client);
    }, [updateToken]);
    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = initClient;
        document.head.appendChild(script);
        return () => {
            document.head.removeChild(script);
        };
    }, [initClient]);
    const login = useCallback(() => {
        authMode.current = "login";
        codeClient?.requestCode();
        return Promise.resolve();
    }, [codeClient]);
    const signup = useCallback(() => {
        authMode.current = "signup";
        codeClient?.requestCode();
        return Promise.resolve();
    }, [codeClient]);
    const logout = useCallback(() => {
        storage.clear();
        window.location.reload();
    }, []);
    const value = useMemo(() => ({ token, error, login, signup, logout, authenticated: !!token }), [token, error, login, signup, logout]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error("useAuth must be used within a AuthProvider");
    }
    return context;
};
